import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import type { components } from '@/api/schema'

import type {
  AwaitingReplyFrom,
  DeskAgentSuggestion,
  DeskAwaitingFilter,
  DeskConversationDetail,
  DeskConversationFilter,
  DeskConversationList,
  DeskConversationSummary,
  DeskMessage,
  DeskMessageAuthor,
  DeskMessageKind,
  DeskTicket,
} from './types'

/** `int64` chega como `number | string` (precisao); os ids de conversa cabem no intervalo seguro -> coerimos. */
function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value)
}

function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

function mapTicket(
  dto: components['schemas']['DeskTicketResponse'] | null | undefined,
): DeskTicket | null {
  if (dto === null || dto === undefined) {
    return null
  }

  return {
    number: toNumber(dto.number),
    status: dto.status,
    reason: dto.reason,
    summary: dto.summary,
    openedWhileUnavailable: dto.openedWhileUnavailable,
    createdAt: dto.createdAt,
    assignedUserId: dto.assignedUserId,
    assignedToName: dto.assignedToName,
    firstResponseAt: dto.firstResponseAt,
  }
}

const AWAITING_VALUES: readonly AwaitingReplyFrom[] = ['none', 'owner', 'contact']

function toAwaiting(value: string): AwaitingReplyFrom {
  return (AWAITING_VALUES as readonly string[]).includes(value)
    ? (value as AwaitingReplyFrom)
    : 'none'
}

function mapSummary(
  dto: components['schemas']['DeskConversationSummaryResponse'],
): DeskConversationSummary {
  return {
    id: toNumber(dto.id),
    status: dto.status,
    contactName: dto.contactName,
    lastMessagePreview: dto.lastMessagePreview,
    lastActivityAt: dto.lastActivityAt,
    ticket: mapTicket(dto.ticket),
    awaitingReplyFrom: toAwaiting(dto.awaitingReplyFrom),
    unansweredSince: dto.unansweredSince,
    lastViewedAt: dto.lastViewedAt,
    triageExcluded: dto.triageExcluded,
    contactIsImportant: dto.contactIsImportant,
  }
}

const MESSAGE_AUTHORS: readonly DeskMessageAuthor[] = [
  'contact',
  'agent',
  'operator',
  'operator_phone',
]
const MESSAGE_KINDS: readonly DeskMessageKind[] = [
  'text',
  'audio',
  'image',
  'video',
  'document',
  'sticker',
  'location',
  'contact',
  'other',
]

function toAuthor(value: string): DeskMessageAuthor {
  return (MESSAGE_AUTHORS as readonly string[]).includes(value)
    ? (value as DeskMessageAuthor)
    : 'contact'
}

function toKind(value: string): DeskMessageKind {
  return (MESSAGE_KINDS as readonly string[]).includes(value) ? (value as DeskMessageKind) : 'other'
}

function mapMessage(dto: components['schemas']['DeskMessageResponse']): DeskMessage {
  return {
    id: toNumber(dto.id),
    content: dto.content,
    visibility: dto.visibility,
    direction: dto.direction,
    createdAt: dto.createdAt,
    authorType: toAuthor(dto.authorType),
    kind: toKind(dto.kind),
    transcript: dto.transcript,
    hasMedia: dto.hasMedia,
    mediaFileName: dto.mediaFileName,
    mediaMimeType: dto.mediaMimeType,
  }
}

function mapSuggestion(
  dto: components['schemas']['DeskAgentSuggestionResponse'],
): DeskAgentSuggestion {
  return {
    executionId: dto.executionId,
    mode: dto.mode,
    succeeded: dto.succeeded,
    suggestedReply: dto.suggestedReply,
    decisionType: dto.decisionType,
    reasonCode: dto.reasonCode,
    riskLevel: dto.riskLevel,
    citations: dto.citations,
    posted: dto.posted,
    autoReplied: dto.autoReplied,
    trace: {
      provider: dto.trace.provider,
      model: dto.trace.model,
      promptTokens: dto.trace.promptTokens === null ? null : toNumber(dto.trace.promptTokens),
      completionTokens:
        dto.trace.completionTokens === null ? null : toNumber(dto.trace.completionTokens),
      durationMs: toNumber(dto.trace.durationMs),
      correlationId: dto.trace.correlationId,
    },
    createdAt: dto.createdAt,
  }
}

/** Lista as conversas do tenant no filtro pedido, com o modo vigente do agente. */
export async function listConversations(
  tenantId: string,
  filter: DeskConversationFilter,
  awaiting: DeskAwaitingFilter = null,
): Promise<DeskConversationList> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/desk/conversations', {
    params: {
      path: { tenantId },
      query: awaiting === null ? { status: filter } : { status: filter, awaiting },
    },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return {
    agentMode: result.data.agentMode,
    conversations: result.data.conversations.map(mapSummary),
  }
}

/** Abre uma conversa do tenant: mensagens + sugestoes/traces do agente. */
export async function getConversation(
  tenantId: string,
  conversationId: number,
): Promise<DeskConversationDetail> {
  const result = await apiClient.GET(
    '/api/v1/tenants/{tenantId}/desk/conversations/{conversationId}',
    { params: { path: { tenantId, conversationId } } },
  )

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return {
    id: toNumber(result.data.id),
    status: result.data.status,
    contactName: result.data.contactName,
    contactId: result.data.contactId,
    agentMode: result.data.agentMode,
    messages: result.data.messages.map(mapMessage),
    agentSuggestions: result.data.agentSuggestions.map(mapSuggestion),
    ticket: mapTicket(result.data.ticket),
    awaitingReplyFrom: toAwaiting(result.data.awaitingReplyFrom),
    unansweredSince: result.data.unansweredSince,
    lastViewedAt: result.data.lastViewedAt,
    triageExcluded: result.data.triageExcluded,
    contactIsImportant: result.data.contactIsImportant,
  }
}

/** Resposta PUBLICA ao cliente, iniciada pelo operador humano. */
export async function replyToConversation(
  tenantId: string,
  conversationId: number,
  content: string,
): Promise<void> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/desk/conversations/{conversationId}/reply',
    { params: { path: { tenantId, conversationId } }, body: { content } },
  )

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/** Nota PRIVADA (interna) do operador — nunca entregue ao cliente. */
export async function addInternalNote(
  tenantId: string,
  conversationId: number,
  content: string,
): Promise<void> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/desk/conversations/{conversationId}/notes',
    { params: { path: { tenantId, conversationId } }, body: { content } },
  )

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/** Resolve ou reabre a conversa. */
export async function setConversationStatus(
  tenantId: string,
  conversationId: number,
  action: 'resolve' | 'reopen',
): Promise<void> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/desk/conversations/{conversationId}/status',
    { params: { path: { tenantId, conversationId } }, body: { action } },
  )

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/** O atendente assume a conversa: abre/reusa o chamado e o atribui a si (a IA fica pausada nela). */
export async function takeOverConversation(
  tenantId: string,
  conversationId: number,
): Promise<void> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/desk/conversations/{conversationId}/take-over',
    { params: { path: { tenantId, conversationId } } },
  )

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/** Encerra o chamado ativo e devolve a conversa para a IA. */
export async function returnConversationToAgent(
  tenantId: string,
  conversationId: number,
): Promise<void> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/desk/conversations/{conversationId}/return-to-agent',
    { params: { path: { tenantId, conversationId } } },
  )

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/**
 * URL do binário de uma mídia da conversa, servido pela própria plataforma (mesma origem; a sessão vai no cookie).
 * O navegador carrega direto em <img>/<audio>/<video>; nada aqui fala com o provedor do canal.
 */
export function conversationMediaUrl(
  tenantId: string,
  conversationId: number,
  messageId: number,
): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantId)}/desk/conversations/${String(conversationId)}/messages/${String(messageId)}/media`
}
